import { $api } from "@/utils/fetch.ts";
import type { LocalizedString } from "@/utils/localized.ts";

export const integrationsQueryOptions = () =>
	$api.queryOptions("get", "/api/integrations");

export const useUpdateIntegrationMutation = () =>
	$api.useMutation("put", "/api/integrations/{id}");

/**
 * Permit or prohibit an integration for the whole org (I13) — the org-level decision,
 * separate from `useUpdateIntegrationMutation` (which stores a tile's org settings).
 * Non-destructive: prohibiting hides the integration and refuses its tool calls, it does
 * not disconnect or delete anything.
 */
export const useSetIntegrationAllowedMutation = () =>
	$api.useMutation("put", "/api/integrations/{id}/allowed");

/**
 * The org's connector-delegated External-API entries (#2107) — DERIVED from the active
 * integrations, not created here. There is deliberately no create or delete mutation: an
 * entry exists exactly while its integration does, and it holds no credential to manage.
 */
export const apiBridgeEntriesQueryOptions = () =>
	$api.queryOptions("get", "/api/integrations/api-bridge");

/** The two properties a derived entry owns: switched on, and whether writes are allowed. */
export const useSetApiBridgeMutation = () =>
	$api.useMutation("put", "/api/integrations/{id}/api-bridge");

// ─── personal enable/disable across ALL integrations (B-mute-2) ───────────────
// `my` lists every integration the caller can reach (any type='integration' space
// they hold a permission on, and connector-package connections) with their own
// mute state. `mute` is the user-owned,
// purely SUBTRACTIVE toggle: it can silence an integration they can reach, never
// grant one they cannot.

export const integrationsMyQueryOptions = () =>
	$api.queryOptions("get", "/api/integrations/my");

/**
 * One row from `/api/integrations/my` — an integration the caller can actually use.
 *
 * One row per card: a connection, an integration the org provides (including a connector
 * package with no connection, whose tools are in every chat), or one the caller could
 * connect.
 *
 * Hand-written, matching `OrganizationMember` in `members-service.ts`: the repo states row
 * shapes rather than deriving them from `openapi.gen.ts`.
 */
export interface MyIntegration {
	/**
	 * Per-CARD id: the integration space id where there is one, else the tile id. Used as
	 * the React key and as the mutation target.
	 */
	id: string;
	/** The tile id / provider — what the icon and the provider badge key on. */
	provider: string;
	/**
	 * Which card this is, decided server-side:
	 *  - `connection` — the caller's own connection. Rename, mute, disconnect.
	 *  - `org-provided` — an integration space someone else set up (an org MCP server;
	 *    mute only), or a connector package with no connection. There is nothing of
	 *    theirs to disconnect.
	 *  - `available` — connectable and not connected yet. Connect only.
	 */
	kind: "connection" | "org-provided" | "available";
	/**
	 * The name the user gave this connection, or null when it has no space. The card
	 * titles itself `connectionName ?? localized(name)` — the locale choice belongs on
	 * this side, which is the only one that knows the user's language.
	 */
	connectionName: string | null;
	name: LocalizedString;
	description?: LocalizedString;
	category: string;
	riskTier?: string;
	keyMode?: "quota" | "byok";
	/** Per project (link it in) vs everywhere (unless the org prohibits it). */
	toolScope?: "project" | "org";
	/**
	 * What the assistant can do with it. The CARDS no longer render this — it is shown
	 * once, in the New-integration wizard, where a user is deciding.
	 */
	tools: {
		name: string;
		displayName: LocalizedString;
		description: LocalizedString;
	}[];
	/** The caller's personal mute of {@link muteTarget}. */
	muted: boolean;
	/**
	 * WHERE this card's switch writes, or null when it cannot be switched at all —
	 * read it rather than deriving one from `integrationSpaceId` or `kind`.
	 */
	muteTarget: { kind: "space"; id: string } | null;
	/**
	 * The rename target, and what a project links. Null for `available` cards and for
	 * a connector package with no connection — {@link muteTarget} owns the switch.
	 */
	integrationSpaceId: string | null;
	/**
	 * For a tools-only connector-package connection: the space whose deletion
	 * disconnects it. Null otherwise.
	 */
	disconnectSpaceId: string | null;
	/** How many projects this connection feeds, for the card's meta line. */
	linkedProjectCount: number;
}

export const useIntegrationMuteMutation = () =>
	$api.useMutation("put", "/api/integrations/{integrationSpaceId}/mute");
